import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  RefreshCw, Mic, MicOff, Share2, Sparkles, Users, LayoutGrid, Music2,
  ChevronDown, ChevronUp, X, Heart, UserPlus, Eye, Gift, Send, Radio,
  Camera as CameraIcon, Layers as LayersIcon, Settings as SettingsIcon,
  Film, BookOpen, FlipHorizontal2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import MobileGreenScreenBar from "./MobileGreenScreenBar";
import StreamHealthPill, { QUALITY_PRESETS } from "./StreamHealthPill";

/**
 * MobileLiveStudio — TikTok-style full-screen mobile live studio.
 *
 * Visual layout matches the user's reference screenshots:
 *   • Top bar: channel badge · follower count · guest avatars · viewer count · X
 *   • Full-bleed live preview
 *   • Right-side vertical icon column (Flip, Mic, Share, Effects, Guests,
 *     Layout, Music, More) — each one drives an existing piece of state
 *     in DirectorsMode so no functionality is lost
 *   • Bottom-left scrolling chat with avatars
 *   • Bottom thin tray: Scene · Cameras · Overlays · Settings
 *   • Bottom action bar: comment input + people · gift · share · Go LIVE button
 *
 * All functionality flows through `broadcasterProxy` + the callback props
 * the parent (DirectorsMode) already wires for the legacy
 * `MobileLivePreviewBar`. Everything that was reachable from the old
 * sticky preview bar remains reachable here.
 *
 * Props: see the JSDoc on the function.
 */
export default function MobileLiveStudio({
  active,
  broadcasterProxy,
  webrtcMode,
  viewerCount = 0,
  followerCount = 0,
  chat = [],
  guests = [],
  fxSettings,
  onStartCamera,
  onGoLive,
  onEndCamera,
  onEndStream,
  onSwitchTab,
  onOpenFx,
  onOpenSceneSwitcher,
  onOpenCameraSwitcher,
  onOpenOverlays,
  onOpenLayoutPicker,
  onOpenSettings,
  onOpenTutorial,
  onSendComment,
  onSendTip,
  onClose,
}) {
  const videoRef = useRef(null);
  const chatRef = useRef(null);
  // `rawStream` — the untouched camera MediaStream from the broadcaster,
  // used as the SOURCE for the Mobile GS compositor. Must never be the
  // composited output, or the GS pipeline would loop back into itself.
  const [rawStream, setRawStream] = useState(null);
  // `previewStream` — what the local preview <video> actually shows.
  // Equals the composited output while GS is ON (so the host sees
  // their own background replaced), else falls back to the raw camera.
  const [previewStream, setPreviewStream] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [commentDraft, setCommentDraft] = useState("");
  const [collapseSideRail, setCollapseSideRail] = useState(false);
  const [followed, setFollowed] = useState(false);
  // Mirror-preview toggle — flips the local `<video>` horizontally so
  // the host sees themselves like a bathroom mirror (turn left → face
  // moves LEFT on screen). This is a LOCAL-ONLY effect — the outbound
  // stream to viewers is unaffected. Front-facing cameras default to
  // MIRRORED because that's what native camera apps do (feels natural
  // to creators). Rear-camera mode auto-flips OFF (there's no mirror
  // metaphor for what someone is filming).
  const [mirrorPreview, setMirrorPreview] = useState(() => {
    try {
      const raw = localStorage.getItem("tv_mirror_preview");
      return raw === null ? true : raw === "1";
    } catch { return true; }
  });
  const toggleMirror = () => {
    setMirrorPreview((v) => {
      const next = !v;
      try { localStorage.setItem("tv_mirror_preview", next ? "1" : "0"); } catch { /* quota */ }
      return next;
    });
  };

  // Stream quality preset — drives camera reacquire dims/fps, the GS
  // compositor's captureStream fps, and the encoder's maxFramerate
  // (read from localStorage in LiveBroadcaster's pcSetup).
  const [qualityKey, setQualityKey] = useState(() => {
    try { return localStorage.getItem("tv_stream_quality_key") || "auto"; } catch { return "auto"; }
  });
  const qualityCfg = QUALITY_PRESETS[qualityKey] || QUALITY_PRESETS.auto;
  const applyQuality = (key, cfg) => {
    setQualityKey(key);
    try {
      localStorage.setItem("tv_stream_quality_key", key);
      localStorage.setItem("tv_stream_quality", JSON.stringify(cfg));
    } catch { /* quota */ }
    try {
      broadcasterProxy?.reacquireCamera?.({ width: cfg.width, height: cfg.height, fps: cfg.fps });
      toast.success(`Quality: ${cfg.label}`);
    } catch { /* non-fatal */ }
  };

  // Mirror the broadcaster's streams into our previews — polls every
  // 600 ms (cheap) so we pick up FX / camera-flip swaps without a full
  // re-render. We track TWO streams: `rawStream` (input to the mobile
  // GS compositor) and `previewStream` (what viewers + the host see —
  // includes the GS composite when active). Fixed iter200: previously
  // the host preview always showed the raw camera even after selecting
  // a background, because we polled getMediaStream() (raw) instead of
  // getPreviewStream() (composited).
  useEffect(() => {
    if (!broadcasterProxy) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const raw = broadcasterProxy.getMediaStream?.() || null;
      const preview = broadcasterProxy.getPreviewStream?.() || raw;
      // Compare video track identity — cheap + stable.
      const prevRawId = rawStream?.getVideoTracks?.()[0]?.id || null;
      const nextRawId = raw?.getVideoTracks?.()[0]?.id || null;
      if (prevRawId !== nextRawId || (!rawStream && raw) || (rawStream && !raw)) {
        setRawStream(raw);
      }
      const prevPreviewId = previewStream?.getVideoTracks?.()[0]?.id || null;
      const nextPreviewId = preview?.getVideoTracks?.()[0]?.id || null;
      if (prevPreviewId !== nextPreviewId || (!previewStream && preview) || (previewStream && !preview)) {
        setPreviewStream(preview);
      }
      // Keep the mic-on indicator in sync — the broadcaster may toggle
      // itself (e.g. push-to-talk hotkey) without going through us.
      const mo = broadcasterProxy.getMicOn?.();
      if (typeof mo === "boolean") setMicOn(mo);
    };
    tick();
    const id = setInterval(tick, 600);
    return () => { cancelled = true; clearInterval(id); };
  }, [broadcasterProxy, rawStream, previewStream]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (previewStream && v.srcObject !== previewStream) {
      try { v.srcObject = previewStream; v.play().catch(() => {}); } catch { /* ignore */ }
    } else if (!previewStream && v.srcObject) {
      try { v.srcObject = null; } catch { /* ignore */ }
    }
  }, [previewStream]);

  // Auto-scroll chat to the newest message.
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chat.length]);

  // Force portrait full-HD camera constraints (1080×1920, 30fps) as
  // soon as the broadcaster is mounted on a phone. iter213: bumped from
  // 720×1280 — 2.25× more source pixels makes the composite visibly
  // sharper; MediaPipe cost is unchanged (it segments at 256×256
  // internally) and the canvas is GPU-accelerated. Phones that can't
  // do 1080 fall back gracefully (constraints use `ideal`).
  const portraitAppliedRef = useRef(false);
  useEffect(() => {
    if (!broadcasterProxy || !rawStream || portraitAppliedRef.current) return;
    if (typeof window === "undefined") return;
    const isPortrait = window.innerWidth < 1024;
    if (!isPortrait) return;
    portraitAppliedRef.current = true;
    // Slight delay so the first acquire has fully resolved (avoids a
    // race where reacquire fires before the broadcaster's media hook
    // is ready). ideal=720x1280 / max=1080x1920 — every modern phone
    // honours one of those.
    setTimeout(() => {
      try {
        const cfg = QUALITY_PRESETS[qualityKey] || QUALITY_PRESETS.auto;
        broadcasterProxy.reacquireCamera?.({
          width: cfg.width,
          height: cfg.height,
          fps: cfg.fps,
        });
      } catch { /* non-fatal */ }
    }, 600);
  }, [broadcasterProxy, rawStream]);

  const onAir = webrtcMode && !!rawStream;

  // Debug HUD — enable by adding ?debug=gs to the URL. Renders a small
  // overlay on the stage showing state flow (bgMode, MediaPipe ready,
  // composite track ID, subject canvas size) so we can diagnose GS
  // pipeline issues on real devices without opening DevTools.
  const debugHud = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const q = new URLSearchParams(window.location.search);
      return q.get("debug") === "gs" || q.get("debug") === "1";
    } catch { return false; }
  }, []);
  const [hud, setHud] = useState({ bg: "?", ready: false, tid: null, subj: "-" });
  useEffect(() => {
    if (!debugHud) return undefined;
    const id = setInterval(() => {
      try {
        const raw = JSON.parse(localStorage.getItem("tapin_greenscreen_settings_v3") || "{}");
        const compTrack = broadcasterProxy?.getCompositedTrack?.() || null;
        setHud({
          bg: raw?.bgMode ? `${raw.bgMode}${raw.bgMode === "blur" ? "/" + (raw.blurLevel || "med") : ""}` : "off",
          enabled: !!raw?.enabled,
          tid: compTrack?.id?.slice(0, 6) || null,
          ready: !!(compTrack && compTrack.readyState === "live"),
          trackW: compTrack?.getSettings?.()?.width || "-",
          trackH: compTrack?.getSettings?.()?.height || "-",
          smart: !!raw?.smartFrame,
          fm: raw?.frameMode || "auto",
          tu: !!raw?.touchUp,
        });
      } catch { /* ignore */ }
    }, 500);
    return () => clearInterval(id);
  }, [debugHud, broadcasterProxy]);
  // Also flip the global GS debug flag so useGreenScreen logs to console.
  useEffect(() => {
    if (debugHud && typeof window !== "undefined") {
      try { window.__TAPIN_FX_DEBUG = 1; } catch { /* ignore */ }
    }
  }, [debugHud]);

  const sideIcons = [
    { key: "flip", label: "Flip", icon: RefreshCw, onClick: () => broadcasterProxy?.flipCamera?.() },
    { key: "mic",  label: micOn ? "Mic" : "Muted", icon: micOn ? Mic : MicOff,
      activeStyle: !micOn,
      onClick: () => {
        broadcasterProxy?.toggleMic?.();
        // Optimistic — the real value comes back on the next tick poll.
        setMicOn((v) => !v);
      } },
    { key: "share", label: "Share", icon: Share2, onClick: () => doShare(active) },
    { key: "fx",    label: "Effects", icon: Sparkles, onClick: onOpenFx,
      activeStyle: !!fxSettings?.chroma?.enabled },
    { key: "guests", label: "Guests", icon: Users, onClick: () => onSwitchTab?.("guests"),
      badge: guests?.length || 0 },
    { key: "layout", label: "Layout", icon: LayoutGrid, onClick: onOpenLayoutPicker },
    { key: "music",  label: "Music",  icon: Music2,    onClick: () => onSwitchTab?.("audio") },
  ];

  const bottomTray = [
    { key: "scene",    label: "Scene",    icon: Film,        onClick: onOpenSceneSwitcher },
    { key: "cameras",  label: "Cameras",  icon: CameraIcon,  onClick: onOpenCameraSwitcher },
    { key: "overlays", label: "Overlays", icon: LayersIcon,  onClick: onOpenOverlays },
    { key: "settings", label: "Settings", icon: SettingsIcon, onClick: onOpenSettings },
  ];

  const doShare = async (s) => {
    const url = `${window.location.origin}/live/${s?.id || ""}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: s?.title || "Tap In TV Live", url });
        return;
      } catch { /* user dismissed */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const sendComment = async () => {
    const body = commentDraft.trim();
    if (!body || !active?.id) return;
    setCommentDraft("");
    try {
      // Backend chat endpoint expects `message`, not `body` — sending
      // the wrong key returns 422 and the host silently can't chat.
      if (onSendComment) await onSendComment(body);
      else await api.post(`/live/${active.id}/chat`, { message: body });
    } catch (e) {
      toast.error("Couldn't send comment");
    }
  };

  const toggleFollow = async () => {
    setFollowed((v) => !v);
    // Most followed creators carry a `creator_id` on the stream row.
    try {
      if (active?.creator_id) await api.post(`/users/${active.creator_id}/follow`).catch(() => {});
    } catch { /* non-fatal */ }
  };

  return (
    <div
      data-testid="mobile-live-studio"
      className="fixed inset-0 z-[60] bg-black text-white flex flex-col lg:hidden"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        // 100dvh tracks iOS Safari's dynamic toolbar so the bottom
        // action bar is never hidden behind it. Browsers without dvh
        // drop the invalid value and fall back to inset-0.
        height: "100dvh",
      }}
    >
      {/* === TOP BAR ============================================== */}
      {/* `min-w-0` on every flexible child + a shorter channel pill so
          the avatars, viewer count and close button NEVER overflow off
          the right edge on 360–414px phones. */}
      <header className="flex items-center gap-1.5 px-2 py-2 shrink-0 [@media(max-height:700px)]:py-1">
        <div className="flex items-center gap-1.5 bg-white/5 rounded-full pl-1 pr-2 py-1 min-w-0 max-w-[44%]">
          <img
            src="/tapintv-logo-official.png"
            alt=""
            className="w-7 h-7 rounded-full object-cover shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="min-w-0">
            <div data-testid="studio-channel-name" className="font-bold text-[12px] leading-tight truncate">
              {active?.creator_name || "Tap In TV"}
            </div>
            <div className="text-[10px] text-white/55 flex items-center gap-1 leading-tight">
              <Heart className="w-2.5 h-2.5 fill-current text-red-400" />
              {formatNum(followerCount || active?.creator_follower_count || 0)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={toggleFollow}
          aria-pressed={followed}
          data-testid="studio-follow-btn"
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
            followed ? "bg-white/20 text-white" : "bg-white text-tv-live"
          }`}
        >
          {followed ? <Heart className="w-4 h-4 fill-current" /> : <UserPlus className="w-4 h-4" />}
        </button>
        <div className="flex-1 min-w-[8px]" />
        {guests.length > 0 && (
          <div className="flex -space-x-2 shrink-0" data-testid="studio-guest-avatars">
            {guests.slice(0, 3).map((g) => (
              <img
                key={g.id || g.user_id || g.name}
                src={g.avatar_url || g.user_avatar || "/tapintv-logo-official.png"}
                alt=""
                className="w-6 h-6 rounded-full border-2 border-black object-cover bg-white/10"
              />
            ))}
          </div>
        )}
        <span className="inline-flex items-center gap-1 bg-white/10 px-2 py-1 rounded-full text-[11px] font-bold shrink-0">
          <Eye className="w-3 h-3 text-white/70" /> {formatNum(viewerCount)}
        </span>
        {/* Mirror-preview toggle — local-only, doesn't affect viewers.
            Defaults ON for front camera (natural mirror UX). */}
        <button
          type="button"
          onClick={toggleMirror}
          aria-pressed={mirrorPreview}
          data-testid="studio-mirror-toggle"
          title={mirrorPreview
            ? "Preview is mirrored — tap to see yourself un-flipped (matches viewers)"
            : "Preview is un-flipped — tap to mirror (like a real mirror)"}
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border ${
            mirrorPreview
              ? "bg-tv-accent/20 text-tv-accent border-tv-accent/40"
              : "bg-white/5 text-white/60 border-white/15 hover:bg-white/10"
          }`}
        >
          <FlipHorizontal2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onClose || onEndStream}
          data-testid="studio-close-btn"
          aria-label="Close studio"
          className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      {/* === STAGE ================================================= */}
      <div className="relative flex-1 mx-3 mb-3 rounded-2xl overflow-hidden bg-[#0B1120] [@media(max-height:700px)]:mx-2 [@media(max-height:700px)]:mb-1.5">
        {onAir ? (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            data-testid="studio-preview-video"
            className={`w-full h-full object-cover ${mirrorPreview ? "-scale-x-100" : ""}`}
          />
        ) : (
          <>
            {active?.thumbnail_url ? (
              <img src={active.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#160d28] via-[#0e1129] to-black" />
            )}
            <div className="absolute inset-0 flex items-center justify-center flex-col gap-3 px-6 text-center">
              <button
                type="button"
                onClick={() => {
                  onStartCamera?.();
                  setTimeout(() => onGoLive?.(), 80);
                }}
                data-testid="studio-start-camera-btn"
                className="bg-tv-live hover:bg-red-600 text-white font-black uppercase tracking-wider text-xs px-6 py-3 rounded-full inline-flex items-center gap-1.5 shadow-[0_0_30px_rgba(220,38,38,0.6)]"
              >
                <Radio className="w-4 h-4" /> Start Camera
              </button>
              <button
                type="button"
                onClick={onOpenTutorial}
                data-testid="studio-tutorial-btn"
                className="text-white/65 hover:text-white text-[11px] font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
              >
                <BookOpen className="w-3.5 h-3.5" /> Learn how to go LIVE
              </button>
            </div>
          </>
        )}

        {/* LIVE pill + viewer count, top-left of the stage; the FX
            status pill stacks under it so it never collides with the
            right-side icon rail. */}
        {onAir && (
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5 z-10">
            <div className="flex items-center gap-1.5">
              <span data-testid="studio-live-pill" className="bg-tv-live text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-white tv-pulse" /> LIVE
              </span>
              <span className="bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded inline-flex items-center gap-1">
                <Eye className="w-3 h-3" /> {formatNum(viewerCount)}
              </span>
            </div>
            {fxSettings?.chroma?.enabled && (
              <div
                data-testid="studio-fx-pill"
                className="bg-emerald-500/90 backdrop-blur-sm rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-black text-white inline-flex items-center gap-1"
              >
                <Sparkles className="w-2.5 h-2.5" />
                FX · {(fxSettings.chroma.mode || "person") === "person" ? "Cutout" : "GS"}
              </div>
            )}
            <StreamHealthPill
              broadcasterProxy={broadcasterProxy}
              qualityKey={qualityKey}
              onSelectQuality={applyQuality}
            />
          </div>
        )}

        {/* Debug HUD — only visible when ?debug=gs. Shows regardless
            of on-air state so the user can inspect pipeline state
            during setup as well as during broadcast. */}
        {debugHud && (
          <div
            data-testid="studio-debug-hud"
            className="absolute top-3 right-14 z-20 bg-black/80 text-emerald-300 text-[9px] leading-tight font-mono px-2 py-1 rounded border border-emerald-400/50 pointer-events-none"
          >
            <div>onAir={onAir ? "Y" : "N"} gs.on={hud.enabled ? "Y" : "N"}</div>
            <div>bg={hud.bg} ready={hud.ready ? "Y" : "N"}</div>
            <div>tid={hud.tid || "-"} {hud.trackW}×{hud.trackH}</div>
            <div>sf={hud.smart ? "Y" : "N"} fm={hud.fm} tu={hud.tu ? "Y" : "N"}</div>
          </div>
        )}

        {/* === RIGHT-SIDE VERTICAL ICON COLUMN ====================== */}
        {/* Constrained max-height + internal scroll so short phones (e.g.
            iPhone SE) still see every control. The rail lives ABOVE the
            video via z-10 and never gets clipped because we set an
            explicit cap rather than relying on top/bottom inset alone. */}
        <div
          data-testid="studio-side-rail"
          className="absolute top-2 right-2 z-10 flex flex-col items-center gap-2.5 max-h-[calc(100%-1rem)] overflow-y-auto no-scrollbar py-1"
        >
          {!collapseSideRail && sideIcons.map(({ key, label, icon: Icon, onClick, activeStyle, badge }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              data-testid={`studio-rail-${key}`}
              aria-label={label}
              className="flex flex-col items-center group shrink-0"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center backdrop-blur transition relative ${
                activeStyle ? "bg-tv-accent text-black" : "bg-black/55 group-hover:bg-black/75 text-white"
              }`}>
                <Icon className="w-[18px] h-[18px]" />
                {badge > 0 && (
                  <span className="absolute -top-1 -right-1 bg-tv-live text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                    {badge}
                  </span>
                )}
              </div>
              <span className="text-[9px] font-bold text-white/90 mt-0.5 drop-shadow">{label}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => setCollapseSideRail((v) => !v)}
            data-testid="studio-rail-toggle"
            aria-label={collapseSideRail ? "Show studio controls" : "Hide studio controls"}
            className="w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center backdrop-blur shrink-0"
          >
            {collapseSideRail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* === LIVE CHAT (bottom-left, narrower so it never overlaps the
             side icon rail on small phones) ========================== */}
        {onAir && chat.length > 0 && (
          <div
            ref={chatRef}
            data-testid="studio-chat-stack"
            className="absolute bottom-3 left-3 right-[68px] max-h-[38%] overflow-y-auto no-scrollbar flex flex-col gap-1.5 pr-1"
            style={{ maskImage: "linear-gradient(to bottom, transparent 0%, black 25%)" }}
          >
            {chat.slice(-10).map((m, i) => (
              <ChatRow key={(m.id || i) + ":" + i} msg={m} />
            ))}
          </div>
        )}
      </div>

      {/* === GREEN SCREEN INLINE BAR ============================== */}
      {/* Lives between the chat overlay and the bottom tray so the user
          can dial in their cutout without leaving the broadcast view.
          Wraps the `useGreenScreen` compositor and bubbles the produced
          track up to the broadcaster via its proxy.replaceVideoTrack
          path. When GS is off, the bar collapses to a single ON toggle
          row so it doesn't eat vertical space. */}
      {webrtcMode && rawStream && broadcasterProxy && (
        <MobileGreenScreenBar
          cameraStream={rawStream}
          captureFps={qualityCfg.fps}
          onCompositedTrack={(track) => {
            try { broadcasterProxy?.setCompositedTrack?.(track || null); }
            catch { /* parent may be unmounting */ }
          }}
          onOpenAdvanced={onOpenFx}
        />
      )}

      {/* === BOTTOM THIN TRAY ====================================== */}
      <div data-testid="studio-tray" className="px-3 pb-2 shrink-0 [@media(max-height:700px)]:pb-1">
        <div className="bg-white/5 border border-white/10 rounded-full px-1.5 py-1.5 flex items-center justify-between gap-1">
          {bottomTray.map(({ key, label, icon: Icon, onClick }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              data-testid={`studio-tray-${key}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-bold text-white/85 hover:text-white py-1 rounded-full hover:bg-white/10 transition"
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      {/* === BOTTOM ACTION BAR ===================================== */}
      <div className="px-2 pb-3 shrink-0 [@media(max-height:700px)]:pb-1.5">
        <div className="flex items-center gap-1.5">
          <div className="flex-1 min-w-0 bg-white/8 rounded-full px-3 py-2 flex items-center gap-1.5">
            <input
              type="text"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              placeholder="Add comment..."
              data-testid="studio-comment-input"
              onKeyDown={(e) => { if (e.key === "Enter") sendComment(); }}
              className="bg-transparent outline-none flex-1 min-w-0 text-sm text-white placeholder:text-white/40"
            />
            <button
              type="button"
              onClick={() => onSwitchTab?.("guests")}
              data-testid="studio-action-guests"
              aria-label="Manage guests"
              className="text-white/75 hover:text-white shrink-0"
            >
              <Users className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onSendTip?.()}
              data-testid="studio-action-tip"
              aria-label="Tips & gifts"
              className="text-white/75 hover:text-yellow-300 shrink-0"
            >
              <Gift className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => doShare(active)}
              data-testid="studio-action-share"
              aria-label="Share"
              className="text-white/75 hover:text-white shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          {onAir ? (
            <button
              type="button"
              onClick={onEndStream}
              data-testid="studio-go-live-btn"
              className="bg-tv-live hover:bg-red-600 text-white font-display font-black uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-full inline-flex items-center gap-1 shrink-0"
            >
              <Radio className="w-3.5 h-3.5" /> End
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                // "Go LIVE" must do two things: mount the broadcaster
                // (camera permission + WebRTC peer) AND fire
                // startBroadcast() so the host actually registers with
                // the WS hub and viewers stop seeing "Waiting for host".
                onStartCamera?.();
                // Slight delay so LiveBroadcaster has time to mount
                // before we tell it to go live.
                setTimeout(() => onGoLive?.(), 80);
              }}
              data-testid="studio-go-live-btn"
              className="bg-tv-live hover:bg-red-600 text-white font-display font-black uppercase tracking-wider text-[11px] px-3.5 py-2.5 rounded-full inline-flex items-center gap-1 shrink-0 shadow-[0_0_18px_rgba(220,38,38,0.45)]"
            >
              <Radio className="w-3.5 h-3.5" /> Go LIVE
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatRow({ msg }) {
  return (
    <div className="flex items-start gap-2 bg-black/35 backdrop-blur-sm rounded-full pl-1 pr-3 py-1 max-w-full">
      {msg.user_avatar ? (
        <img src={msg.user_avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
      ) : (
        <div className="w-6 h-6 rounded-full bg-white/20 text-[10px] font-bold flex items-center justify-center shrink-0">
          {(msg.user_name || "?").charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[11px] font-bold text-white/90 leading-tight truncate">{msg.user_name || "Guest"}</div>
        <div className="text-[12px] text-white leading-tight break-words">{msg.body || msg.text || ""}</div>
      </div>
    </div>
  );
}

function formatNum(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(v);
}
